import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type JSX,
} from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { NotebookPen, User, UserCheck, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { logError } from '@lib/logger/logError';
import { logSuccess } from '@lib/logger/logSuccess';

export function useDebouncedCallback<P extends any[], R> (
  fn: (...args: P) => R,
  delay = 1200
): (...args: P) => void {
  const fnRef = useRef(fn);
  const tRef = useRef<number | null>(null);
  const lastArgsRef = useRef<P | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const clear = useCallback((): void => {
    if (tRef.current !== null) {
      window.clearTimeout(tRef.current);
      tRef.current = null;
    }
  }, []);

  const run = useCallback((...args: P): void => {
    lastArgsRef.current = args;
    clear();
    tRef.current = window.setTimeout(() => {
      lastArgsRef.current = null;
      fnRef.current(...args);
    }, delay) as unknown as number;
  }, [clear, delay]);

  // Flush any pending call on unmount to prevent data loss
  useEffect(() => {
    return () => {
      if (tRef.current !== null && lastArgsRef.current !== null) {
        window.clearTimeout(tRef.current);
        tRef.current = null;
        fnRef.current(...lastArgsRef.current);
        lastArgsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return run;
}

interface Props {
  contactId?: string | null;
  note?: string | null;
  agentNote?: string | null;
  sdrNote?: string | null;
  /** If true, the textarea edits the SDR note; if false, the agent note */
  isSDR?: boolean;
  isTransferred: boolean;
  activeVoicemail: boolean;
  onSaveAgentNote?: (text: string) => Promise<void> | void;
  onSaveSdrNote?: (text: string) => Promise<void> | void;
  className?: string;
}

const safeText = (v: string | null | undefined, fallback: string): string => {
  const t = (v ?? '').trim();
  return t.length > 0 ? t : fallback;
};

function getErrorMessage (e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  if (typeof e === 'string') {
    return e;
  }
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const maybe = e as { message?: unknown };
    if (typeof maybe.message === 'string') {
      return maybe.message;
    }
  }
  return 'Error';
}

const NotesCard = ({
  contactId,
  note,
  agentNote,
  sdrNote,
  isSDR = false,
  onSaveAgentNote,
  onSaveSdrNote,
  isTransferred,
  activeVoicemail,
  className,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<string>('');
  const [savedTick, setSavedTick] = useState<boolean>(false);

  const currentServerValue = isSDR ? (sdrNote ?? '') : (agentNote ?? '');

  useEffect(() => {
    setDraft(currentServerValue);
  }, [contactId, isSDR, currentServerValue]);

  const dirty = useMemo<boolean>(
    () => (draft ?? '') !== (currentServerValue ?? ''),
    [draft, currentServerValue]
  );

  const showSaved = useCallback((): () => void => {
    setSavedTick(true);
    const id = window.setTimeout(() => {
      setSavedTick(false);
    }, 700);
    return () => {
      window.clearTimeout(id);
    };
  }, []);

  const persist = useCallback(
    async (text: string): Promise<void> => {
      try {
        if (isSDR) {
          if (onSaveSdrNote == null) {
            return;
          }
          await onSaveSdrNote(text);
        } else {
          if (onSaveAgentNote == null) {
            return;
          }
          await onSaveAgentNote(text);
        }
        showSaved();
        await logSuccess(
          t('dialer.notes.savedLog', 'Note saved'),
          'NotesCard/persist',
          { isSDR, contactId },
          false
        );
      } catch (e: unknown) {
        await logError(
          t('dialer.notes.saveError', { defaultValue: 'Error saving note: {{msg}}', msg: getErrorMessage(e) }),
          'NotesCard/persist',
          { isSDR, contactId },
          true
        );
      }
    },
    [isSDR, onSaveAgentNote, onSaveSdrNote, showSaved, contactId, t]
  );

  const debouncedSave = useDebouncedCallback<[string], Promise<void>>(
    async (text) => {
      if (!dirty) {
        return;
      }
      await persist(text);
    },
    1200
  );

  useEffect(() => {
    debouncedSave(draft);
  }, [draft, debouncedSave]);

  const liveAgentNote = isSDR
    ? (agentNote ?? '')
    : (dirty ? draft : (agentNote ?? ''));
  const liveSdrNote = !isSDR
    ? (sdrNote ?? '')
    : (dirty ? draft : (sdrNote ?? ''));

  return (
    <Card className={clsx('bg-card rounded-2xl shadow-sm border border-border md:col-span-1 h-full flex flex-col min-h-0 overflow-hidden',
      isTransferred && 'border-(--ring)',
      !isSDR && isTransferred && 'max-h-[550px]',
      activeVoicemail && 'border-(--purple)',
      className)}>
      <CardHeader className="flex items-center justify-between shrink-0 px-3 md:px-4 py-1.5 md:py-2">
        <CardTitle className="text-sm md:text-base lg:text-lg">{t('dialer.notes.title', 'Notes')}</CardTitle>
        {savedTick && (
          <div className="flex items-center gap-1 text-green-600 text-xs">
            <CheckCircle2 className="size-4" />
            <span>{t('dialer.notes.saved', 'Saved')}</span>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-1.5 md:gap-2 flex-1 min-h-0 overflow-y-auto px-3 md:px-4 pt-0 pb-3 md:pb-4">
        <section>
          <div className="space-y-0.5">
            <span className="text-xs md:text-sm lg:text-base font-medium">
              {t('dialer.notes.mainNote.label', 'Note')}
            </span>
            <p
              className={clsx(
                'text-xs md:text-sm lg:text-base max-h-16 md:max-h-20 overflow-y-auto break-all',
                ((note ?? '').trim() !== '')
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/70 italic'
              )}
            >
              {safeText(note, t('dialer.notes.mainNote.empty', 'No note.'))}
            </p>
          </div>
        </section>

        <section className="flex items-start gap-2">
          <User className="size-4 md:size-5 lg:size-6 text-muted-foreground mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <span className="text-xs md:text-sm lg:text-base font-medium">
              {t('dialer.notes.agent.label', 'Agent note')}
            </span>
            <p
              className={clsx(
                'text-xs md:text-sm lg:text-base max-h-16 md:max-h-20 overflow-y-auto break-all',
                ((liveAgentNote ?? '').trim() !== '')
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/70 italic'
              )}
            >
              {safeText(liveAgentNote, t('dialer.notes.agent.empty', 'No agent note.'))}
            </p>
          </div>
        </section>

        <section className="flex items-start gap-2">
          <UserCheck className="size-4 md:size-5 lg:size-6 text-muted-foreground mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <span className="text-xs md:text-sm lg:text-base font-medium">
              {t('dialer.notes.sdr.label', 'SDR note')}
            </span>
            <p
              className={clsx(
                'text-xs md:text-sm lg:text-base max-h-16 md:max-h-20 overflow-y-auto break-all',
                ((liveSdrNote ?? '').trim() !== '')
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/70 italic'
              )}
            >
              {safeText(liveSdrNote, t('dialer.notes.sdr.empty', 'No SDR note.'))}
            </p>
          </div>
        </section>

        <section className="flex items-center gap-2 text-muted-foreground">
          <NotebookPen className="size-4" />
          <span className="text-xs md:text-sm lg:text-base font-medium">
            {isSDR
              ? t('dialer.notes.editor.sdr', 'Edit SDR note')
              : t('dialer.notes.editor.agent', 'Edit agent note')}
          </span>
        </section>

        <textarea
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setDraft(e.target.value);
          }}
          placeholder={
            isSDR
              ? t('dialer.notes.editor.placeholderSdr', 'Write SDR note...')
              : t('dialer.notes.editor.placeholderAgent', 'Write agent note...')
          }
          className="w-full flex-1 min-h-0 rounded-md border border-border bg-background px-3 py-2 text-sm md:text-base text-foreground resize-none break-all"
        />
      </CardContent>
    </Card>
  );
};

export default NotesCard;
